import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { Trash2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import { Button, Card, CardContent, Tabs, TabsList, TabsTrigger, Alert, AlertDescription, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@ndd/ui";
import { Badge } from "@/components/badge";
import { RatingBadge } from "@/components/scorecard/rating-badge";
import type { RDFeedback, SupervisorFeedback, XmlItemRating } from "../../../../shared/analysis-types";
import { AdminGuard, AdminNav } from "./shared";

type ViewMode = "supervisor" | "rd" | "transcript";

interface MetaInfo {
  id: string;
  createdAt: string;
  notes?: string;
  promptVersion: string;
}

interface TranscriptItem {
  id: string;
  meta: MetaInfo | null;
}

interface TranscriptDetail {
  id: string;
  transcript: string;
  outputs: { meta: MetaInfo; supervisor: SupervisorFeedback; rd: RDFeedback } | null;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) {
    const body = error.body as { error?: string } | undefined;
    if (body?.error) return body.error;
  }
  return error instanceof Error ? error.message : fallback;
}

export function AdminDev(): ReactElement {
  // The dev cache is a local-development tool: production builds serve a 404,
  // mirroring the server's unmounted /api/dev routes.
  if (import.meta.env.PROD) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-lg text-muted-foreground">404 — Not Found</p>
      </div>
    );
  }
  return (
    <AdminGuard>
      <DevCacheList />
    </AdminGuard>
  );
}

export function AdminDevDetail(): ReactElement {
  // The dev cache is a local-development tool: production builds serve a 404,
  // mirroring the server's unmounted /api/dev routes.
  const params = useParams({ strict: false }) as { id?: string };
  if (import.meta.env.PROD) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-lg text-muted-foreground">404 — Not Found</p>
      </div>
    );
  }
  return (
    <AdminGuard>
      <DevCacheDetail id={params.id ?? ""} />
    </AdminGuard>
  );
}

// =============================================================================
// DEV CACHE LIST (legacy /admin/dev)
// =============================================================================

function DevCacheList(): ReactElement {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const transcripts = useQuery({
    queryKey: ["dev-transcripts"],
    queryFn: () => authenticatedApi<{ data: { transcripts: TranscriptItem[] } }>(getToken, "/api/dev/transcripts"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => authenticatedApi(getToken, `/api/dev/transcripts/${id}`, { method: "DELETE" }),
    onMutate: () => setError(null),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["dev-transcripts"] }),
    onError: (mutationError) => setError(apiErrorMessage(mutationError, "Failed to delete transcript")),
  });

  const items = transcripts.data?.data.transcripts ?? [];
  const loading = transcripts.isLoading;
  const deletingId = remove.isPending ? remove.variables : null;

  return (
    <div className="min-h-screen bg-background py-6 px-4">
      <div className="max-w-3xl mx-auto">
        <AdminNav />
        <header className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-foreground font-sans">
              Dev Cache
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Cached transcripts for development and iteration
            </p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm">Back to App</Button>
          </Link>
        </header>

        {loading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground/20"></div>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="rounded-lg border border-border bg-card py-12 text-center">
            <p className="text-muted-foreground mb-1">
              No cached transcripts yet.
            </p>
            <p className="text-sm text-muted-foreground">
              Analyze a transcript and click &ldquo;Save to Dev&rdquo; to cache it here.
            </p>
            <Link to="/" className="inline-block mt-4">
              <Button size="sm">Go to Main App</Button>
            </Link>
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="rounded-lg border border-border overflow-hidden">
            {items.map((item, i) => (
              <div
                key={item.id}
                className={`flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors ${
                  i > 0 ? "border-t border-border" : ""
                } ${deletingId === item.id ? "opacity-50" : ""}`}
              >
                <Link to="/admin/dev/$id" params={{ id: item.id }} className="flex-1 min-w-0">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-medium text-foreground">
                      {item.id}
                    </p>
                    {item.meta && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {new Date(item.meta.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                        {item.meta.notes && (
                          <span className="ml-1.5 text-muted-foreground/70">&middot; {item.meta.notes}</span>
                        )}
                      </p>
                    )}
                    {!item.meta && (
                      <p className="text-xs text-muted-foreground/60 mt-0.5 italic">
                        No outputs cached
                      </p>
                    )}
                  </div>
                </Link>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this transcript?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete{" "}
                          <span className="font-mono">{item.id}</span> and all
                          cached outputs. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => remove.mutate(item.id)}
                          className="bg-destructive text-white hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Link to="/admin/dev/$id" params={{ id: item.id }}>
                    <svg className="w-4 h-4 text-muted-foreground/40" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// DEV CACHE DETAIL (legacy /admin/dev/[id])
// =============================================================================

function DevCacheDetail({ id }: { id: string }): ReactElement {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [activeView, setActiveView] = useState<ViewMode>("supervisor");
  const [rerunError, setRerunError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["dev-transcript", id],
    queryFn: () => authenticatedApi<{ data: TranscriptDetail }>(getToken, `/api/dev/transcripts/${id}`),
  });
  const rerun = useMutation({
    mutationFn: () => authenticatedApi(getToken, `/api/dev/transcripts/${id}/rerun`, { method: "POST" }),
    onMutate: () => setRerunError(null),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["dev-transcript", id] }),
        queryClient.invalidateQueries({ queryKey: ["dev-transcripts"] }),
      ]);
    },
    onError: (error) => setRerunError(apiErrorMessage(error, "Failed to re-run analysis")),
  });

  const loading = detail.isLoading;
  const transcript = detail.data?.data.transcript ?? null;
  const supervisor = detail.data?.data.outputs?.supervisor ?? null;
  const rd = detail.data?.data.outputs?.rd ?? null;
  const meta = detail.data?.data.outputs?.meta ?? null;
  const rerunning = rerun.isPending;
  const error = rerunError ?? (detail.isError ? apiErrorMessage(detail.error, "Failed to load transcript") : null);

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <header className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground font-sans">
              {id}
            </h1>
            {meta && (
              <p className="text-muted-foreground">
                {new Date(meta.createdAt).toLocaleDateString()}{" "}
                {new Date(meta.createdAt).toLocaleTimeString()}
                {meta.notes && ` · ${meta.notes}`}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => rerun.mutate()}
              disabled={rerunning || loading}
            >
              {rerunning ? (
                <>
                  <span className="animate-spin mr-2">⟳</span>
                  Re-running...
                </>
              ) : (
                "Re-run Analysis"
              )}
            </Button>
            <Link to="/admin/dev">
              <Button variant="outline">Back to List</Button>
            </Link>
          </div>
        </header>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Error */}
        {error && (
          <Alert variant="danger">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Content */}
        {!loading && !error && transcript && (
          <div>
            {/* Tab Navigation */}
            <Tabs
              value={activeView}
              onValueChange={(v) => setActiveView(v as ViewMode)}
              className="mb-6"
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="supervisor" disabled={!supervisor}>
                  Supervisor
                </TabsTrigger>
                <TabsTrigger value="rd" disabled={!rd}>
                  RD
                </TabsTrigger>
                <TabsTrigger value="transcript">Transcript</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* View Content */}
            {activeView === "supervisor" && supervisor && (
              <SupervisorView feedback={supervisor} />
            )}
            {activeView === "rd" && rd && <RDView feedback={rd} />}
            {activeView === "transcript" && <TranscriptView transcript={transcript} />}

            {/* No outputs warning */}
            {(activeView === "supervisor" || activeView === "rd") &&
              !supervisor &&
              !rd && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">
                      No cached outputs for this transcript.
                    </p>
                  </CardContent>
                </Card>
              )}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// SUPERVISOR VIEW - With Always List Scorecard
// =============================================================================

// The legacy dev cache stores capitalized rating strings; the shared
// RatingBadge speaks XmlItemRating, so map before rendering. Unmapped ratings
// (e.g. "N/A") render their raw text in a neutral badge, matching the legacy
// gray treatment.
const DEV_RATING_TO_XML: Partial<Record<string, Exclude<XmlItemRating, "na" | "unknown">>> = {
  "Exceeds": "exceeds",
  "Good": "great",
  "Great": "great",
  "Meets": "meets",
  "Not there Yet": "not_yet",
  "Needs Improvement": "needs_improvement",
};

function DevRatingBadge({ rating }: { rating: string }): ReactElement {
  const mapped = DEV_RATING_TO_XML[rating];
  if (!mapped) {
    return (
      <Badge variant="muted" size="md" className="w-20 justify-center">
        {rating}
      </Badge>
    );
  }
  return <RatingBadge rating={mapped} />;
}

function SupervisorView({ feedback }: { feedback: SupervisorFeedback }): ReactElement {
  return (
    <div className="space-y-8">
      {/* Header: Session type + Overall Rating */}
      <p className="text-sm text-muted-foreground">
        {feedback.session_type === "initial" ? "Initial Evaluation" : "Follow-Up"} ·{" "}
        {feedback.overall_rating}
      </p>

      {/* Clinical Impression */}
      <section>
        <p className="text-lg leading-relaxed text-foreground">
          {feedback.clinical_impression}
        </p>
      </section>

      {/* Primary: Supervision Focus */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="pt-5 pb-5">
          <p className="text-xs font-medium text-primary uppercase tracking-wide mb-2">
            Supervision Focus
          </p>
          <p className="text-foreground font-medium">{feedback.supervision_focus}</p>
        </CardContent>
      </Card>

      {/* Always List Scorecard */}
      {feedback.always_list && feedback.always_list.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-foreground mb-4">Always List Scorecard</h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-2 px-3 font-medium text-muted-foreground">Item</th>
                  <th className="text-left py-2 px-3 font-medium text-muted-foreground w-28">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {feedback.always_list.map((item, i) => (
                  <tr key={i} className="hover:bg-muted/20">
                    <td className="py-2 px-3">
                      <p className="font-medium text-foreground">{item.item}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.evidence}</p>
                      {item.note && (
                        <p className="text-xs text-muted-foreground/70 mt-0.5 italic">{item.note}</p>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <DevRatingBadge rating={item.rating} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Strengths */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3">Strengths</h3>
        <ul className="space-y-2">
          {feedback.strengths.map((strength, i) => (
            <li key={i} className="text-sm text-foreground leading-relaxed">
              {strength}
            </li>
          ))}
        </ul>
      </section>

      {/* Growth Areas */}
      {feedback.growth_areas.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-foreground mb-3">Growth Areas</h3>
          <ul className="space-y-2">
            {feedback.growth_areas.map((area, i) => (
              <li key={i} className="text-sm text-foreground leading-relaxed">
                {area}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Coaching Notes */}
      <section className="pt-4 border-t border-border">
        <h3 className="text-sm font-semibold text-muted-foreground mb-3">
          Coaching Notes
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
          {feedback.coaching_notes}
        </p>
      </section>

      {/* Flags */}
      {feedback.flags && feedback.flags.length > 0 && (
        <Alert variant="danger" className="mt-6">
          <AlertDescription className="space-y-3">
            <p className="text-xs font-medium uppercase tracking-wide">Flags</p>
            {feedback.flags.map((flag, i) => (
              <div key={i}>
                <p className="font-medium">{flag.concern}</p>
                <p className="text-sm opacity-80 mt-1">{flag.context}</p>
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// =============================================================================
// RD VIEW - Redesigned for Scannability
// =============================================================================

function RDView({ feedback }: { feedback: RDFeedback }): ReactElement {
  return (
    <div className="space-y-8">
      {/* Header: Session type */}
      <p className="text-sm text-muted-foreground">
        {feedback.session_type === "initial" ? "Initial Evaluation" : "Follow-Up"}
      </p>

      {/* 1. POSITIVE: Main Feedback Narrative (starts with strengths) */}
      <section>
        <ReactMarkdown
          components={{
            p: ({ children }) => (
              <p className="text-base leading-relaxed text-foreground mb-4 last:mb-0">
                {children}
              </p>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold">{children}</strong>
            ),
            em: ({ children }) => (
              <em className="italic">{children}</em>
            ),
          }}
        >
          {feedback.feedback}
        </ReactMarkdown>
      </section>

      {/* 2. POSITIVE: Highlights - What went well (numbered) */}
      {feedback.highlights.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-foreground mb-4">Highlights</h3>
          <div className="space-y-4">
            {feedback.highlights.map((highlight, i) => (
              <div key={i} className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center">
                  {i + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium text-foreground text-sm">{highlight.moment}</p>
                  <p className="text-sm text-muted-foreground mt-1">{highlight.impact}</p>
                  {highlight.quote && (
                    <p className="text-sm italic text-muted-foreground border-l-2 border-primary/40 pl-3 mt-2">
                      &ldquo;{highlight.quote}&rdquo;
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 3. CONSTRUCTIVE: Gaps (separate cards), Try This, Focus Area */}
      {(feedback.always_list_gaps?.length || feedback.try_this || feedback.focus_area) && (
        <section>
          {/* Gaps to Address - each gap is its own card */}
          {feedback.always_list_gaps && feedback.always_list_gaps.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-4">Gaps to Address</h3>
              <div className="space-y-3">
                {feedback.always_list_gaps.map((gap, i) => (
                  <Card key={i} className="border-warning/30 bg-warning/5">
                    <CardContent className="py-3 px-4">
                      <p className="text-base font-semibold text-foreground">{gap.item}</p>
                      <p className="text-sm text-muted-foreground mt-1">{gap.moment}</p>
                      <p className="text-sm text-warning mt-2">→ {gap.note}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Try This - Specific coaching moment */}
          {feedback.try_this && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-4">Try This Next Time</h3>
              <Card className="border-muted">
                <CardContent className="py-4 space-y-3">
                  <p className="text-sm text-muted-foreground">{feedback.try_this.situation}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="bg-muted/30 rounded-md p-3">
                      <p className="text-xs text-muted-foreground mb-1">You said:</p>
                      <p className="text-sm text-foreground">&ldquo;{feedback.try_this.current}&rdquo;</p>
                    </div>
                    <div className="bg-primary/5 rounded-md p-3">
                      <p className="text-xs text-primary mb-1">Try instead:</p>
                      <p className="text-sm text-foreground font-medium">&ldquo;{feedback.try_this.alternative}&rdquo;</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Focus Area */}
          {feedback.focus_area && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Focus Area</h3>
              <Card className="border-muted">
                <CardContent className="py-4">
                  <p className="text-foreground">{feedback.focus_area}</p>
                </CardContent>
              </Card>
            </div>
          )}
        </section>
      )}

      {/* 4. POSITIVE: Encouragement (closing) */}
      {feedback.encouragement && (
        <section className="pt-4 border-t border-border">
          <p className="text-base text-foreground italic">
            {feedback.encouragement}
          </p>
        </section>
      )}
    </div>
  );
}

// =============================================================================
// TRANSCRIPT VIEW
// =============================================================================

function TranscriptView({ transcript }: { transcript: string }): ReactElement {
  return (
    <div className="font-mono text-sm whitespace-pre-wrap text-foreground bg-muted/30 p-4 rounded-md">
      {transcript}
    </div>
  );
}
